import type { CoverageEligibilityResponse } from '@medplum/fhirtypes'
import { formatDateTime } from '@/utils.ts'

export type CoverageStatusLabel = 'Active' | 'Not Active' | 'Unknown'

export type EligibilityDecisionMode = 'approve' | 'disapprove'

export type ParsedEligibilityDecision = {
  mode: EligibilityDecisionMode
  reason: string
  notes: string
  reviewedBy: string
  reviewedOn: string
}

const MANUAL_DISAPPROVAL_PREFIX = 'Manual disapproval recorded'
const MANUAL_APPROVAL_PREFIXES = ['Manual approval recorded', 'Manual approved by']
const ISO_UTC_TIMESTAMP_REGEX = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z\b/g

export function parseEligibilityDecisionDisposition(
  disposition?: string | null,
): ParsedEligibilityDecision | null {
  const mode = disposition?.startsWith(MANUAL_DISAPPROVAL_PREFIX)
    ? 'disapprove'
    : MANUAL_APPROVAL_PREFIXES.some((prefix) => disposition?.startsWith(prefix))
      ? 'approve'
      : null

  if (!mode || !disposition) {
    return null
  }

  const lines = disposition.split('\n')
  const getLineValue = (prefix: string) =>
    lines
      .find((line) => line.startsWith(prefix))
      ?.slice(prefix.length)
      .trim() || ''

  return {
    mode,
    reason: getLineValue('Reason:'),
    notes: getLineValue('Notes:') || getLineValue('CC Action:'),
    reviewedBy: getLineValue('Reviewed by:'),
    reviewedOn: getLineValue('Reviewed on:'),
  }
}

export function localizeDispositionTimestamps(disposition?: string | null): string {
  if (!disposition) {
    return ''
  }

  return disposition.replace(ISO_UTC_TIMESTAMP_REGEX, (timestamp) => {
    const localized = formatDateTime(timestamp, '-', true, true)
    return localized || timestamp
  })
}

export function getCoverageStatus(
  coverageResponse: CoverageEligibilityResponse | null,
): CoverageStatusLabel {
  if (coverageResponse?.insurance?.[0]?.inforce === true) {
    return 'Active'
  }

  if (coverageResponse?.insurance?.[0]?.inforce === false) {
    return 'Not Active'
  }

  return 'Unknown'
}

export function getCoverageStatusColor(status: CoverageStatusLabel): string {
  switch (status) {
    case 'Active':
      return 'green'
    case 'Not Active':
      return 'red'
    case 'Unknown':
    default:
      return 'gray'
  }
}
